import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/format'
import {
  getDirectSaleOrder,
  type DirectSaleOrderDto,
} from '@/lib/api/direct-sale-order'

interface DirectSaleOrderSectionProps {
  projectId: string
}

export function DirectSaleOrderSection({ projectId }: DirectSaleOrderSectionProps) {
  const navigate = useNavigate()
  const [order, setOrder] = useState<DirectSaleOrderDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadOrder = useCallback(async () => {
    try {
      setError(null)
      const data = await getDirectSaleOrder(projectId)
      setOrder(data)
    } catch {
      setError('Erreur lors du chargement de la commande')
    } finally {
      setIsLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void loadOrder()
  }, [loadOrder])

  return (
    <section id="direct-sale-order" className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Commande</h2>
          <p className="text-sm text-muted-foreground">Résumé de la commande directe</p>
        </div>

        {order && !order.isLocked && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/commande-directe`)}
          >
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Modifier la commande
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-muted-foreground">
          {error}
        </div>
      ) : order ? (
        <Card>
          <CardContent className="p-4 space-y-4">
            {/* Status badge */}
            <div className="flex items-center gap-2">
              {order.isLocked ? (
                <Badge variant="outline" className="gap-1 text-muted-foreground">
                  <Lock className="h-3 w-3" />
                  Commande clôturée
                </Badge>
              ) : order.status === 'confirmed' ? (
                <Badge className="border-0 bg-green-100 text-green-700">
                  Commande validée
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  Brouillon
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {order.lines.length} ligne{order.lines.length !== 1 ? 's' : ''}
              </span>
            </div>

            {order.lines.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucune ligne — cliquez sur "Modifier la commande" pour ajouter des articles.
              </p>
            ) : (
              <div className="space-y-2">
                {/* VAT breakdown */}
                {order.vatBreakdown.length > 0 && (
                  <div className="space-y-1">
                    {order.vatBreakdown.map((vat) => (
                      <div key={vat.vatPercent} className="flex justify-between text-sm text-muted-foreground">
                        <span>TVA {vat.vatPercent}% sur {formatCurrency(vat.taxableAmount)}</span>
                        <span>{formatCurrency(vat.taxAmount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Totals */}
                <div className="border-t pt-2 space-y-1">
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Total HTVA</span>
                    <span>{formatCurrency(order.totalWithoutTax)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold">
                    <span>Total TTC</span>
                    <span>{formatCurrency(order.totalWithTax)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </section>
  )
}
